import {ulid} from "ulidx";
import ClientModule from "@/helpers/Modules/Single/ClientModule";
import PolicyModule from "@/helpers/Modules/Single/PolicyModule";
import VehicleModule from "@/helpers/Modules/Single/VehicleModule";
import ValidatorModule from "@/helpers/Modules/Single/ValidatorModule";
import { resolveMode } from "@/helpers/Modules/Mode";
import permissions from '@/helpers/permissions';

export default class BpmOsgpoModule {
    /** Код продукта в policy JSON и draft API */
    static PRODUCT = 'OGPO_WEB_EA'

    /** false → открыть BPM для groupReserveSale() [0, 3, 7, 8] */
    static ADMIN_ONLY = true

    /**
     * Возвращает класс режима работы с полисом (оформление / переоформление / …).
     * Все режим-зависимые правила и точки расширения — в BaseMode и его наследниках.
     *
     * Если для ОСГПО понадобится продуктовая специфика поверх общего режима —
     * достаточно создать класс `BpmOsgpoRewriteMode extends RewriteMode` и
     * вернуть его здесь для `type === 'rewrite'`.
     *
     * @param {string|null|undefined} type — значение `$route.query.type`.
     * @returns {Function} класс режима (наследник BaseMode).
     */
    static resolveMode(type) {
        return resolveMode(type)
    }

    /**
     * Применяет декоратор текущего режима к базовым настройкам полей формы.
     * Если режим не передан — возвращает исходные settings без изменений.
     * Используется всеми `*FieldsSettings(...)` как финальный шаг.
     *
     * @param {string} scope     — область применения ('clientData', 'clientSearch' и т.п.)
     * @param {Object} settings  — базовый объект настроек { field: {required, show, disabled} }
     * @param {Object} ctx       — контекст ({ mode, clientType, policy, ... })
     */
    static applyModeToFields(scope, settings, ctx = {}) {
        const mode = ctx.mode
        if (!mode || typeof mode.decorateFields !== 'function') return settings
        return mode.decorateFields(scope, settings, ctx)
    }
    static Modal = class {
        /**
         * Проверяет, относится ли переданный ключ к статичному инфо-блоку карточки клиента.
         * Используется для условного отображения кратких атрибутов (ИИН, тип, резидентство, класс).
         * @param {string} val - Ключ атрибута.
         * @returns {boolean} true, если относится к инфо-карточке.
         */
        static infoCard(val) {
            const data = ['iin', 'person', 'resident', 'class', 'pensioner']
            return data.includes(val)
        }
    }

    static Policy = class extends PolicyModule {
        /**
         * Возвращает пустой шаблон полиса с дефолтными значениями и ULID-номером.
         * Используется для инициализации формы оформления ОСГПО.
         * @param {string|false} today - Дата начала (или false, если не задано).
         * @param {*} product - Идентификатор/описание продукта.
         * @returns {Object} Пустой полис.
         */
        static empty(today = false, product = BpmOsgpoModule.PRODUCT) {
            return {
                number:            ulid(),
                product:           product,
                product_type:      7,
                business_process:  [],
                insurer:           null,
                insureds:          [],
                vehicle:           [],
                manager:           null,
                agent:             null,
                apc:               null,
                treaty: {
                    date:           today ?? '',
                    term:           '',
                },
                calculationDate:   null,
                premium:           null,
                premiumCalculation: null,
                paymentPhone:      null,
                clientAddedList:   [],
                carAddedList:      [],
                ip:                null,
                dgpo:              null,
                wizardStep:        0,
                rewrite: {
                    contract: null,
                    linked: []
                },
                scoring: null,
            }
        }
    }

    static Treaty = class {
        static DEFAULT_DURATION = { type: 'month', value: 12 }
        static MIN_DURATION = { type: 'month', value: 6 }
        static MAX_DURATION = { type: 'month', value: 12 }

        static END_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

        static ALL_DURATIONS = [
            { id: { type: 'month', value: 6 }, title: '6 месяцев' },
            { id: { type: 'month', value: 7 }, title: '7 месяцев' },
            { id: { type: 'month', value: 8 }, title: '8 месяцев' },
            { id: { type: 'month', value: 9 }, title: '9 месяцев' },
            { id: { type: 'month', value: 10 }, title: '10 месяцев' },
            { id: { type: 'month', value: 11 }, title: '11 месяцев' },
            { id: { type: 'month', value: 12 }, title: '12 месяцев' },
        ]

        static allowedMonths(mode) {
            if (!mode || typeof mode.getTreatyTermsDictionary !== 'function') {
                return this.ALL_DURATIONS.map(item => item.id.value)
            }
            return Object.keys(mode.getTreatyTermsDictionary()).map(Number)
        }

        static durationsDictionary(mode) {
            const allowed = this.allowedMonths(mode)
            return this.ALL_DURATIONS.filter(item => allowed.includes(item.id.value))
        }

        static minDuration(mode) {
            const months = this.allowedMonths(mode)
            return { type: 'month', value: Math.min(...months) }
        }

        static maxDuration(mode) {
            const months = this.allowedMonths(mode)
            return { type: 'month', value: Math.max(...months) }
        }

        static isEndDate(value) {
            return typeof value === 'string' && this.END_DATE_RE.test(value)
        }

        static calculateEndDate(startDate, duration) {
            if (!startDate || !duration) {
                return ''
            }
            // YYYY-MM-DD парсится как полночь UTC — считаем тоже в UTC, иначе результат зависит от пояса браузера
            const date = new Date(startDate)
            if (Number.isNaN(date.getTime())) {
                return ''
            }
            if (duration.type === 'day') {
                date.setUTCDate(date.getUTCDate() + duration.value)
                date.setUTCDate(date.getUTCDate() - 1)
            } else if (duration.type === 'month') {
                date.setUTCMonth(date.getUTCMonth() + duration.value)
                if (duration.value !== 6) {
                    date.setUTCDate(date.getUTCDate() - 1)
                }
            }
            return date.toISOString().split('T')[0]
        }

        static maxEndDate(startDate, mode) {
            return this.calculateEndDate(startDate, mode ? this.maxDuration(mode) : this.MAX_DURATION)
        }
        static minEndDate(startDate, mode) {
            return this.calculateEndDate(startDate, mode ? this.minDuration(mode) : this.MIN_DURATION)
        }
    }

    static Dgpo = class {
        static empty() {
            return {
                sum: 2000000,
                av: 0,
                agent: null 
            }
        }

        static sumDictionary() {
            return [
                { value: 1000000, text: '1 000 000' },
                { value: 2000000, text: '2 000 000' },
                { value: 3000000, text: '3 000 000' },
                { value: 4000000, text: '4 000 000' },
            ]
        }

        static avDictionary() {
            return [
                { value: 0,    text: '0%' },
                { value: 10,   text: '10%' },
                { value: 15,   text: '15%' },
                { value: 20,   text: '20%' },
                { value: 25,   text: '25%' },
                { value: 30,   text: '30%' },
                { value: 35,   text: '35%' },
                { value: 40,   text: '40%' },
                { value: 45,   text: '45%' },
                { value: 50,   text: '50%' },
                { value: 55,   text: '55%' },
                { value: 60,   text: '60%' },
                { value: 65,   text: '65%' },
                { value: 70,   text: '70%' }
            ];
        }

    }

    static PremiumCalculation = class {
        static getProductSummary(calculation, product) {
            const summary = calculation?.[product]
            if (!summary || typeof summary !== 'object') {
                return null
            }

            return {
                conclusion: Number(summary.conclusion) || 0,
                rewrite: Number(summary.rewrite) || 0,
                addPremium: Number(summary.addPremium) || 0,
                unearnedPremium: Number(summary.unearnedPremium) || 0,
            }
        }

        static hasProductSummary(summary) {
            if (!summary) {
                return false
            }

            return summary.conclusion > 0
                || summary.rewrite > 0
                || summary.addPremium > 0
                || summary.unearnedPremium > 0
        }

        static totalsFromDetails(details, product) {
            let conclusion = 0
            let rewrite = 0

            for (const item of details) {
                if (item.product !== product) continue
                const amount = Math.abs(Number(item.premium) || 0)
                if (item.operation === 'rewrite' || Number(item.premium) < 0) {
                    rewrite += amount
                } else {
                    conclusion += amount
                }
            }

            return { conclusion, rewrite }
        }

        static normalizeCalculation(calculation) {
            if (!calculation) {
                return null
            }

            if (calculation.OGPO && calculation.DGPO) {
                return calculation
            }

            const details = Array.isArray(calculation.details) ? calculation.details : []
            if (!details.length) {
                return calculation
            }

            const normalized = { ...calculation }

            for (const product of ['OGPO', 'DGPO']) {
                const { conclusion, rewrite } = this.totalsFromDetails(details, product)
                normalized[product] = {
                    conclusion,
                    rewrite,
                    addPremium: Math.max(0, conclusion - rewrite),
                    unearnedPremium: Math.max(0, rewrite - conclusion),
                }
            }

            return normalized
        }


        static buildProductRows(insuranceSum, summary) {
            if (!this.hasProductSummary(summary)) {
                return []
            }

            const rows = []

            rows.push({
                key: 'insurance-premium',
                label: 'Страховая премия',
                value: summary.conclusion,
                showCurrency: true,
                divider: rows.length > 0,
                color: 'primary',
            })

            if (summary.rewrite > 0) {
                rows.push({
                    key: 'rewrite-premium',
                    label: 'Остаток от расторжения',
                    value: summary.rewrite,
                    showCurrency: true,
                    divider: rows.length > 0,
                    color: null,
                })
            }

            if (summary.unearnedPremium > 0) {
                rows.push({
                    key: 'refund',
                    label: 'Возврат',
                    value: summary.unearnedPremium,
                    showCurrency: true,
                    divider: true,
                    color: 'danger',
                })
            } 

            rows.push({
                key: 'to-pay',
                label: 'Итого к оплате',
                value: summary.addPremium,
                showCurrency: true,
                divider: true,
                color: 'primary',
                highlight: true,
            })

            return rows
        }

        static getEffectiveCalculation(calculation, policy) {
            if (calculation) {
                return this.normalizeCalculation(calculation)
            }

            if (policy?.premium == null && !policy?.calculationDate) {
                return null
            }

            return {
                premium: policy.premium,
                addPremium: policy.premium,
                calculationDate: policy.calculationDate,
            }
        }

        static getOgpoRows(calculation, policy) {
            const calc = this.getEffectiveCalculation(calculation, policy)
            if (!calc) {
                return []
            }

            const summary = this.getProductSummary(calc, 'OGPO')
            if (!this.hasProductSummary(summary)) {
                return []
            }

            return this.buildProductRows(null, summary)
        }

        static getDgpoRows(calculation, policy) {
            const calc = this.getEffectiveCalculation(calculation, policy)
            if (!calc) {
                return []
            }

            const summary = this.getProductSummary(calc, 'DGPO')
            if (!this.hasProductSummary(summary)) {
                return []
            }

            return this.buildProductRows(policy?.dgpo?.sum ?? null, summary)
        }

        static getSummary(calculation, policy) {
            const calc = this.getEffectiveCalculation(calculation, policy)
            if (!calc) {
                return {
                    total: null,
                    refund: 0,
                    hasDetails: false,
                }
            }

            const total = Number(calc.premium ?? calc.addPremium ?? policy?.premium)
            const refund = Number(calc.unearnedPremium) || 0

            return {
                total: Number.isFinite(total) ? total : null,
                refund,
                hasDetails: this.getOgpoRows(calculation, policy).length > 0
                    || this.getDgpoRows(calculation, policy).length > 0,
            }
        }

        static formatMoney(value) {
            if (value === null || value === undefined) return '—'
            const n = Number(value)
            if (!Number.isFinite(n)) return '—'
            return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Math.abs(n))
        }
    }

    static BusinessProcess = class {
        static normalizeStatus(status) {
            return Number.isInteger(Number(status)) ? Number(status) : 1
        }

        static isPendingConfirmation(process) {
            return this.normalizeStatus(process?.status) === 0
        }

        static isReady(process) {
            return this.normalizeStatus(process?.status) === 1
        }

        static isStarted(process) {
            return this.normalizeStatus(process?.status) === 2
        }

        static isCompleted(process) {
            return this.normalizeStatus(process?.status) === 3
        }

        static isActive(process) {
            return this.normalizeStatus(process?.status) !== 3
        }

        /**
         *  types
         *  1 - Страхователь
         *  2 - Застрахованный
         *  3 - Авто
         *  4 - Калькуляция
         *  5 - Оформление полиса
         */

        static defaultProcessData() {
            return {
                id: null,
                status: 'CREATED',
                message: 'Ожидание запуска процесса...',
                waiting_action: null,
                data: null,
            }
        }

        static create({ withProcessData = true } = {}) {
            return {
                id: ulid(),
                type: null,
                status: 1,
                init_data: null,
                process_data: withProcessData ? this.defaultProcessData() : null,
                meta: {},
                updated_at: new Date().toISOString()
            }
        }

        /**
         * Обновляет отметку времени изменения процесса.
         * Используется при любом изменении данных процесса (init_data, process_data, meta и т.п.).
         * @param {Object} process - Объект бизнес-процесса.
         */
        static touch(process) {
            if (!process || typeof process !== 'object') {
                return
            }
            process.updated_at = new Date().toISOString()
        }
    }

    static Client = class extends ClientModule {
        /**
         * Возвращает пустой шаблон клиента с разделами client/filter/search/location/document.
         * Используется при создании страхователя/застрахованных в полисе.
         * @param {number|null} clientType - Тип клиента: 1 — страхователь, 2 — застрахованный, null — default.
         * @returns {Object} Пустая структура клиента.
         */
        static empty(clientType = null) {
            return {
                search: {
                    phone:         clientType === null ? '7'   : BpmOsgpoModule.Client.searchFieldsSettings(clientType).phone.value,
                    iin:           clientType === null ? ''    : BpmOsgpoModule.Client.searchFieldsSettings(clientType).iin.value,
                    nomer_vu:      clientType === null ? ''    : BpmOsgpoModule.Client.searchFieldsSettings(clientType).nomer_vu.value,
                    isDrive:         clientType === null ? false : BpmOsgpoModule.Client.searchFieldsSettings(clientType).isDrive.value,
                    isSocial:      clientType === null ? false : BpmOsgpoModule.Client.searchFieldsSettings(clientType).isSocial.value,
                },
                client: {
                    activity_kind_id:     '',
                    accountant:           '',
                    patronymic:           '',
                    birthday:             '',
                    surname:              '',
                    email:                '',
                    name:                 '',
                    iin:                  '',
                    economics_sector_id:   10,
                    id:                   null,
                    sex:                  1,
                    phone:                '7',
                    politician:           false,
                    social:               false
                },
                filter: {
                    resident:   clientType === null ? true : BpmOsgpoModule.Client.searchFieldsSettings(clientType).resident.value,
                    person:     clientType === null ? true : BpmOsgpoModule.Client.searchFieldsSettings(clientType).person.value,
                    verify:     clientType === null ? true : BpmOsgpoModule.Client.searchFieldsSettings(clientType).verify.value,
                },
                location: {
                    country_id: 1,
                    address:    '',
                },
                document: {
                    verified_at: '',
                    type_id:     null,
                    number:      '',
                    issued:      '',
                    issuer:      '',
                    until:       ''
                },
                beingVerify: false,
                podft: {
                    success: true,
                    message: '',
                    setDocument: false,
                },
                sanction: [],
            }
        }

        static parseBpmData(data) {
            if (!data || typeof data !== 'object') {
                return null
            }

            const boolVal = (val) => {
                if (val === null || val === undefined) return false
                if (typeof val === 'boolean') return val
                if (typeof val === 'number') return val !== 0
                if (typeof val === 'string') {
                    return ['1', 'true', 'yes'].includes(val.toLowerCase())
                }
                return !!val
            }

            const document = data.document ? [data.document] : null
            const location = (data.country_id !== undefined || data.address !== undefined)
                ? [{
                    country_id: data.country_id ?? null,
                    address:    data.address    ?? null,
                }]
                : null

            const licence = data.licence ?? null

            return {
                filter: {
                    resident: boolVal(data.resident),
                    person:   boolVal(data.person),
                    verify:   boolVal(data.verify),
                },
                client: {
                    title:               data.title               ?? null,
                    surname:             data.surname             ?? null,
                    name:                data.name                ?? null,
                    patronymic:          data.patronymic          ?? null,
                    surname_eng:         data.surname_eng         ?? null,
                    name_eng:            data.name_eng            ?? null,
                    patronymic_eng:      data.patronymic_eng      ?? null,
                    economics_sector_id: data.economics_sector_id ?? null,
                    experience:          data.experience          ?? null,
                    pensioner:           data.pensioner           ?? null,
                    activity_kind_id:    data.activity_kind_id    ?? null,
                    country_id:          data.country_id          ?? null,
                    politician:          false,
                    birthday:            data.birthday            ?? null,
                    address:             data.address             ?? null,
                    phone:               data.phone               ?? null,
                    email:               data.email               ?? null,
                    iin:                 data.iin                 ?? null,
                    sex:                 data.sex                 ?? null,
                    id:                  data.id ?? data.esbd_id  ?? null,
                },
                document:     document ? document[0] : null,
                documents:    document,
                location:     location ? location[0] : null,
                class:        data.class        ?? null,
                licence:      licence,
                sanction:     Array.isArray(data.sanction)
                    ? data.sanction.map((item) => {
                        if (typeof item === 'string') return item
                        return item?.listname ?? ''
                    }).filter(Boolean)
                    : [],
            }
        }

        /**
         * Возвращает конфигурацию обязательности, отображения и блокировки полей клиента по шагу сценария.
         * Используется валидаторами и рендерингом форм.
         * @param {boolean} resident - Флаг резидентства.
         * @param {boolean} person - Флаг физического лица.
         * @param {Object} [ctx] - Контекст режима ({ mode, clientType, ... }).
         * @returns {Object} Настройки полей.
         */
        static dataFieldsSettings(resident, person, ctx = {}) {
            let step;
            switch (true) {
                case (resident && person):
                    step = 0;
                    break;
                case (resident && !person):
                    step = 2;
                    break;
                default:
                    step = 8;
                    break;
            }

            const data = {
                0: { // Резидент Физ
                    economics_sector_id: { required: false, show: false, disabled:true  },
                    activity_kind_id:    { required: false, show: false, disabled:true  },
                    patronymic:          { required: false, show: true,  disabled:true  },
                    birthday:            { required: true,  show: true,  disabled:true  },
                    surname:             { required: true,  show: true,  disabled:true  },
                    email:               { required: false, show: true,  disabled:true  },
                    phone:               { required: true,  show: true,  disabled:true  },
                    name:                { required: true,  show: true,  disabled:true  },
                    iin:                 { required: true,  show: true,  disabled:true  },
                    sex:                 { required: true,  show: true,  disabled:true  },
                    // Document
                    type_id:             { required: true,  show: true, disabled:true },
                    issued:              { required: true,  show: true, disabled:true },
                    number:              { required: true,  show: true, disabled:true },
                    issuer:              { required: true,  show: true, disabled:true },
                    until:               { required: true,  show: true, disabled:true },
                    // Address
                    country_id:          { required: true, show: false, disabled:true },
                    address:             { required: true, show: true,  disabled:true },
                    // Licence
                    licence_number:      { required: false, show: true,  disabled:true },
                    licence_issued:      { required: false, show: true,  disabled:true },
                    licence_until:       { required: false, show: true,  disabled:true },
                    licence_categories:  { required: false, show: true,  disabled:true },
                },
                2: { // Резидент Юр
                    economics_sector_id: { required: true,  show: true,  disabled:true  },
                    activity_kind_id:    { required: true,  show: true,  disabled:true  },
                    patronymic:          { required: false, show: false, disabled:false },
                    birthday:            { required: true,  show: true,  disabled:true  },
                    surname:             { required: false, show: false, disabled:false },
                    email:               { required: true,  show: true,  disabled:false },
                    phone:               { required: false, show: false, disabled:false },
                    name:                { required: true,  show: true,  disabled:true  },
                    iin:                 { required: true,  show: true,  disabled:true  },
                    sex:                 { required: false, show: false, disabled:false },
                    // Document
                    type_id:             { required: false, show: false, disabled:false },
                    issued:              { required: false, show: false, disabled:false },
                    number:              { required: false, show: false, disabled:false },
                    issuer:              { required: false, show: false, disabled:false },
                    until:               { required: false, show: false, disabled:false },
                    // Address
                    country_id:          { required: false, show: false, disabled:false },
                    address:             { required: true,  show: true,  disabled:false },
                    // Licence
                    licence_number:      { required: false, show: false, disabled:false },
                    licence_issued:      { required: false, show: false, disabled:false },
                    licence_until:       { required: false, show: false, disabled:false },
                    licence_categories:  { required: false, show: false, disabled:false },
                },
                8: { // allow all
                    economics_sector_id: { required: false, show: true,  disabled:false },
                    activity_kind_id:    { required: false, show: true,  disabled:false },
                    patronymic:          { required: false, show: true,  disabled:false },
                    birthday:            { required: true,  show: true,  disabled:false },
                    surname:             { required: true,  show: true,  disabled:false },
                    email:               { required: false, show: true,  disabled:false },
                    phone:               { required: false, show: true,  disabled:false },
                    name:                { required: true,  show: true,  disabled:false },
                    iin:                 { required: false, show: true,  disabled:false },
                    sex:                 { required: true,  show: true,  disabled:false },
                    // Document
                    type_id:             { required: true,  show: true,  disabled:false },
                    issued:              { required: true,  show: true,  disabled:false },
                    number:              { required: true,  show: true,  disabled:false },
                    issuer:              { required: true,  show: true,  disabled:false },
                    until:               { required: false, show: true,  disabled:false },
                    // Address
                    country_id:          { required: true,  show: true,  disabled:false },
                    address:             { required: false, show: true,  disabled:false },
                    // Licence
                    licence_number:      { required: false, show: true,  disabled:false },
                    licence_issued:      { required: false, show: true,  disabled:false },
                    licence_until:       { required: false, show: true,  disabled:false },
                    licence_categories:  { required: false, show: true,  disabled:false },
                }
            };

            return BpmOsgpoModule.applyModeToFields('clientData', data[step], ctx);
        }
        /**
         * Возвращает настройки полей модального окна поиска клиента.
         * @param {number} clientType - Тип клиента: 1 — страхователь, 2 — застрахованный.
         * @param {boolean} [person]   - Флаг физического лица.
         * @param {boolean} [resident] - Флаг резидентства.
         * @param {Object}  [ctx]      - Контекст режима ({ mode, clientType, ... }).
         * @returns {Object} Настройки полей поиска.
         */
        static searchFieldsSettings(clientType, person, resident, ctx = {}) {
            // Набор полей одинаковый для страхователя (1) и застрахованного (2);
            // признаки show/required зависят только от person/resident.

            let settings = {};

            if (clientType === 1) { // Страхователь
                settings = {
                    resident: { value: true, disabled: false, required: true, show: true },
                    person:   { value: true, disabled: false, required: true, show: true },
                    verify:   { value: true, disabled: false, required: true, show: true },
                    bin:      { value: '',    disabled: false, required: !person && resident, show: !person && resident },
                    iin:      { value: '',    disabled: false, required:  person && resident, show:  person && resident },
                    phone:    { value: '7',   disabled: false, required:  person && resident, show:  person && resident },
                    nomer_vu: { value: '',    disabled: false, required: false,               show:  person && resident },
                    isDrive:  { value: false, disabled: false, required: false,               show:  person && resident },
                    isSocial: { value: false, disabled: false, required: false,               show:  person && resident },
                };
            } else if (clientType === 2) { // Застрахованный
                settings = {
                    resident: { value: true, disabled: false, required: true, show: true },
                    person:   { value: true, disabled: false, required: true, show: true },
                    verify:   { value: true, disabled: false, required: true, show: true },
                    bin:      { value: '',    disabled: false, required: !person && resident, show: !person && resident },
                    iin:      { value: '',    disabled: false, required:  person && resident, show:  person && resident },
                    phone:    { value: '7',   disabled: false, required:  person && resident, show:  person && resident },
                    nomer_vu: { value: '',    disabled: false, required:  person && resident, show:  person && resident },
                    isDrive:  { value: false, disabled: true,  required: false,               show:  false },
                    isSocial: { value: false, disabled: false, required: false,               show:  person && resident },
                };
            } else {
                settings = {
                    resident: { value: true, disabled: false, required: true, show: true },
                    person:   { value: true, disabled: false, required: true, show: true },
                    verify:   { value: true, disabled: false, required: true, show: true },
                    bin:      { value: '',    disabled: false, required: !person && resident, show: !person && resident },
                    iin:      { value: '',    disabled: false, required:  person && resident, show:  person && resident },
                    phone:    { value: '7',   disabled: false, required:  person && resident, show:  person && resident },
                    nomer_vu: { value: '',    disabled: false, required:  person && resident, show:  person && resident },
                    isDrive:  { value: false, disabled: false, required: false,               show:  false },
                    isSocial: { value: false, disabled: false, required: false,               show:  person && resident },
                };
            }
       
            // Явно оставляем переданный clientType приоритетным:
            // в некоторых сценариях ctx может содержать устаревшее значение (например, 0).
            return BpmOsgpoModule.applyModeToFields('clientSearch', settings, { ...ctx, clientType });
        }

        static searchFields(clientType, person, resident) {
            let settings = BpmOsgpoModule.Client.searchFieldsSettings(clientType, person, resident)
            let fields = {};
            Object.keys(settings).forEach(key => {
                if (settings[key].show) {
                    fields[key] = settings[key].value;
                }
            });
            return fields;
        }

        static filterFieldsSettings(clientType, ctx = {}) {
            const settings = {
                person:         { value: true, disabled: true, required: true,  show: true },
                resident:       { value: true, disabled: true, required: true,  show: true },
                verify:         { value: true, disabled: true, required: true,  show: true },
            };
            return BpmOsgpoModule.applyModeToFields('clientFilter', settings, { ...ctx, clientType });
        }

        static filterFields(clientType, person, resident) {
            let settings = BpmOsgpoModule.Client.filterFieldsSettings(clientType, person, resident)
            let fields = {};
            Object.keys(settings).forEach(key => {
                if (settings[key].show) {
                    fields[key] = settings[key].value;
                }
            });
            return fields;
        }

        /**
         * HELPER
         * Пересчитывает блокировку некоторых полей на основании наличия/отсутствия данных документа.
         * Используется при пост-обработке распознанного документа для дозаполнения формы.
         * Если поле у клиента отсутствует, то поле в форме становится доступным для заполнения (disabled: false).
         * @param {Object} fieldSettings - Текущие настройки полей.
         * @param {Object} data - Полная структура клиента.
         * @returns {Object} Обновлённые настройки полей.
         */
        static recalculateDisabledFields(fieldSettings, data) {
            if (!data.document || data.document.number === null || data.document.number === '' || data.document.number === undefined) return fieldSettings
            let formatedData = this.flattenToValues(data)
            const allowedFields = ['issued', 'sex', 'until', 'issuer', 'address', 'email', 'country_id']
            Object.keys(fieldSettings).forEach(key => {
                if (!allowedFields.includes(key)) return
                if (formatedData[key] === null || formatedData[key] === '' || formatedData[key] === undefined) {
                    fieldSettings[key].disabled = false
                }
            })
            return fieldSettings
        }
    };

    static Vehicle = class extends VehicleModule {
        /**
         * Возвращает пустой шаблон транспортного средства с вложенными блоками двигателя и регистрации.
         * Используется при добавлении ТС в полис.
         * @returns {Object} Пустая структура ТС.
         */
        static empty() {
            return {
                id:        0,
                type_id:   4,
                number:    '',
                vin:       '',
                body:      '',
                chassis:   '',
                year:      '',
                color:     '',
                places:    '',
                category:  '',
                mark_id:   '',
                mark:      '',
                model_id:  '',
                model:     '',
                engine: {
                    number:  '',
                    power:   '',
                    volume:  ''
                },
                registration: {
                    number:      '',
                    certificate: '',
                    date:        '',
                    region_id:   '',
                    big_city:    '',
                    kato:        ''
                },
                verify:      true,
                beingVerify: false,
                podft: {
                    success: true,
                    message: '',
                    setDocument: false,
                }
            }
        }
        /**
         * Возвращает набор полей поиска ТС в зависимости от режима проверки.
         * @param {boolean} type - 0 number, 1 vin.
         * @returns {Object} Поля поиска.
         */
        static searchFieldsSettings(type, ctx = {}) {
            let settings;
            switch (true) {
                case type === 0:
                    settings = {
                        number:      { required: true,  show: true,  disabled: false },
                        vin:         { required: false, show: false, disabled: true  },
                        certificate: { required: true,  show: true,  disabled: false }
                    }
                    break
                case type === 1:
                    settings = {
                        number:      { required: true,  show: true,  disabled: false },
                        vin:         { required: true,  show: true,  disabled: false },
                        certificate: { required: false, show: false, disabled: true  }
                    }
                    break
                default:
                    settings = {
                        number:      { required: true,  show: true,  disabled: false },
                        vin:         { required: true,  show: true,  disabled: false },
                        certificate: { required: true,  show: true,  disabled: false }
                    }
            }
            return BpmOsgpoModule.applyModeToFields('vehicleSearch', settings, ctx)
        }

        static searchFields() {
            return {
                number: null,
                vin: null,
                certificate: null,
            }
        }

        static filterFieldsSettings(ctx = {}) {
            const settings = {
                type:            { required: false, show: true,  disabled: false },
                verify:          { required: false, show: true,  disabled: false },
            }
            return BpmOsgpoModule.applyModeToFields('vehicleFilter', settings, ctx)
        }

        static filterFields() {
            return {
                type: 0,
                verify: true,
            }
        }
        /**
         * Возвращает настройки обязательности/видимости/блокировки полей ТС по шагу сценария.
         *
         * @param {boolean} verify - Флаг резидентности/нерезидентности.
         * @param {Object}  [ctx]  - Контекст режима ({ mode, policy, ... }).
         * @returns {Object} Настройки полей ТС.
         */
        static dataFieldsSettings(verify, ctx = {}) {
            let settings
            switch (true) {
                case verify:
                    settings = { // резидент
                        number:      { required: true,  show: true, disabled: true },
                        vin:         { required: true,  show: true, disabled: true },
                        type_id:     { required: true,  show: true, disabled: true },
                        year:        { required: true,  show: true, disabled: true },
                        mark:        { required: true,  show: true, disabled: true },
                        model:       { required: true,  show: true, disabled: true },
                        color:       { required: false, show: true, disabled: true },
                        places:      { required: false, show: true, disabled: true },
                        category:    { required: false, show: true, disabled: true },
                        body:        { required: false, show: true, disabled: true },
                        chassis:     { required: false, show: true, disabled: true },
                        engine: {
                            number:    { required: false, show: true, disabled: true },
                            power:     { required: false, show: true, disabled: true },
                            volume:    { required: false, show: true, disabled: true },
                        },
                        registration: {
                            number:      { required: true,  show: true, disabled: true },
                            certificate: { required: true,  show: true, disabled: true },
                            date:        { required: true,  show: true, disabled: true },
                            region_id:   { required: false, show: true, disabled: true },
                        }
                    }
                    break
                default:
                    settings = { // нерезидент
                        number:      { required: true,  show: true, disabled: true },
                        vin:         { required: true,  show: true, disabled: true },
                        type_id:     { required: true,  show: true, disabled: true },
                        year:        { required: true,  show: true, disabled: true },
                        mark:        { required: true,  show: true, disabled: true },
                        model:       { required: true,  show: true, disabled: true },
                        color:       { required: false, show: true, disabled: true },
                        places:      { required: false, show: true, disabled: true },
                        category:    { required: false, show: true, disabled: true },
                        body:        { required: false, show: true, disabled: true },
                        chassis:     { required: false, show: true, disabled: true },
                        engine: {
                            number:    { required: false, show: true, disabled: true },
                            power:     { required: false, show: true, disabled: true },
                            volume:    { required: false, show: true, disabled: true },
                        },
                        registration: {
                            number:      { required: true,  show: true, disabled: true },
                            certificate: { required: true,  show: true, disabled: true },
                            date:        { required: true,  show: true, disabled: true },
                            region_id:   { required: false, show: true, disabled: true },
                        }
                    }
            }
            return BpmOsgpoModule.applyModeToFields('vehicleData', settings, ctx)
        }
    };

    static Validator = class extends ValidatorModule {
        static allowedRoles() {
            return BpmOsgpoModule.ADMIN_ONLY
                ? permissions.admin()
                : permissions.groupReserveSale()
        }

        static isUserAllowed(user) {
            if (!user) return false
            if (!this.allowedRoles().includes(user.role)) return false
            if (user.role === 8 && !user.apc) return false
            return true
        }
        /**
         * Проверяет заполненность ключевых полей формы поиска клиента/застрахованного для ОСГПО
         * по его типу (страхователь/застрахованный), признакам резидентства и физлица.
         * Используется для активации кнопки поиска/перехода к следующему этапу.
         *
         * @param {boolean} resident - Флаг резидентства.
         * @param {boolean} person - Флаг физического лица.
         * @param {number} clientType - Тип клиента: 1 — страхователь, 2 — застрахованный.
         * @param {Object} form - Значения полей формы.
         * @returns {boolean} true, если минимально необходимые поля заполнены.
         */
        static isSearchFormFieldsFilled(resident, person, clientType, form, settings) {
            settings = settings || BpmOsgpoModule.Client.searchFieldsSettings(clientType, person, resident)

            if (clientType === 1) { // Страхователь
                switch (true) {
                    // ФЛ-резидент
                    case (resident && person):
                        return (!settings.phone.required || form.phone.length === 16) && (!settings.iin.required || form.iin.length === 15) && (!settings.nomer_vu.required || form.nomer_vu.length >= 6);
                    // ФЛ-нерезидент
                    case (!resident && person):
                        return false; // не поддерживается в ОСГПО
                    // ЮЛ-резидент
                    case (resident && !person):
                        return false; // не поддерживается в ОСГПО
                    // ЮЛ-нерезидент
                    case (!resident && !person):
                        return false; // не поддерживается в ОСГПО
                    default:
                        return false;
                }
            } else if (clientType === 2) { // Застрахованный
                switch (true) {
                    // ФЛ-резидент
                    case (resident && person):
                        return form.phone.length === 16 && form.iin.length === 15 && form.nomer_vu.length >= 6;
                    // ФЛ-нерезидент
                    case (!resident && person):
                        return false; // не поддерживается в ОСГПО
                    // ЮЛ-резидент
                    case (resident && !person):
                        return false; // не поддерживается в ОСГПО
                    // ЮЛ-нерезидент
                    case (!resident && !person):
                        return false; // не поддерживается в ОСГПО
                    default:
                        return false;
                }
            } else {
                return false;
            }
        }
        /**
         * Проверяет, содержит ли объект все обязательные ключи из списка.
         * @param {Object} data - Объект для проверки.
         * @param {string[]} requiredKeys - Список обязательных ключей.
         * @returns {boolean} true, если все ключи присутствуют.
         */
        static hasAllRequiredKeys(data, requiredKeys) {
            if (data && requiredKeys) {
                const dataKeys = Object.keys(data);
                return requiredKeys.every(key => dataKeys.includes(key));
            }
            return false;
        }
        /**
         * Проверяет заполненность всех обязательных полей согласно настройкам required.
         * @param {*} data - Значение поля или объект значений.
         * @param {Object} settings - Настройки required для поля/группы.
         * @returns {boolean} true, если все обязательные поля заполнены.
         */
        static areAllRequiredFieldsFilled(data, settings) {
            const isFilled = (value) => value !== null && value !== undefined && value !== ''
            // eslint-disable-next-line no-prototype-builtins
            if (settings.hasOwnProperty('required')) {
                return !settings.required || isFilled(data);
            } else {
                return Object.entries(settings).every(([field, properties]) => {
                    return !properties.required || isFilled(data[field]);
                });
            }
        }
        /**
         * Определяет, отображать ли кнопку добавления ТС.
         * @param {number|Array} insureds - Количество застрахованных или массив застрахованных.
         * @param {number|Array} vehicle - Количество ТС или массив ТС.
         * @returns {boolean} true если кнопку следует отображать, иначе false.
         */
        static isShowVehicleAddButton(insureds, vehicle, businessProcess) {
            let vehicleCount = Array.isArray(vehicle) ? vehicle.length : vehicle
            let insuredsCount = Array.isArray(insureds) ? insureds.length : insureds
            let vehicleBusinessProcessCount = businessProcess.filter(item => item.type === 3 && BpmOsgpoModule.BusinessProcess.isActive(item)).length
            let insuredsBusinessProcessCount = businessProcess.filter(item => item.type === 2 && BpmOsgpoModule.BusinessProcess.isActive(item)).length
            switch (true) {
                case (vehicleCount + vehicleBusinessProcessCount >= 12): return false
                case (insuredsCount + insuredsBusinessProcessCount >= 2 && vehicleCount + vehicleBusinessProcessCount >= 1): return false
                default: return true
            }
        }

        /**
         * Определяет, отображать ли кнопку добавления застрахованных.
         * @param {number|Array} insureds - Количество застрахованных или массив застрахованных.
         * @param {number|Array} vehicle - Количество ТС или массив ТС.
         * @returns {boolean} true если кнопку следует отображать, иначе false.
         */
        static isShowInsuredsAddButton(insureds, vehicle, businessProcess) {
            let insuredsCount = Array.isArray(insureds) ? insureds.length : insureds
            let vehicleCount = Array.isArray(vehicle) ? vehicle.length : vehicle
            let insuredsBusinessProcessCount = businessProcess.filter(item => item.type === 2 && BpmOsgpoModule.BusinessProcess.isActive(item)).length
            let vehicleBusinessProcessCount = businessProcess.filter(item => item.type === 3 && BpmOsgpoModule.BusinessProcess.isActive(item)).length
            switch (true) {
                case (insuredsCount + insuredsBusinessProcessCount >= 12): return false
                case (vehicleCount + vehicleBusinessProcessCount >= 2 && insuredsCount + insuredsBusinessProcessCount >= 1): return false
                default: return true
            }
        }
        /**
         * Определяет, отображать ли кнопку добавления страхователя.
         * В рамках ОСГПО допускается ровно один страхователь, поэтому
         * дополнительной проверки по количеству не требуется — сам факт
         * отсутствия страхователя проверяется в шаблоне.
         * @param {number|Array} insureds - Количество застрахованных или массив.
         * @param {number|Array} vehicle - Количество ТС или массив.
         * @returns {boolean} true — кнопку можно показывать.
         */
        static isShowInsurerAddButton(insureds, vehicle, businessProcess) {
            // let insuredsCount = Array.isArray(insureds) ? insureds.length : insureds
            // let vehicleCount = Array.isArray(vehicle) ? vehicle.length : vehicle
            let insurerBusinessProcessCount = businessProcess.filter(item => item.type === 1 && BpmOsgpoModule.BusinessProcess.isActive(item)).length
            switch (true) {
                case (insurerBusinessProcessCount >= 1): return false
                default: return true
            }
        }
        /**
         * Проверяет заполнение всех обязательных полей для каждого транспортного средства.
         *
         * Проверяет, что у каждого ТС (vehicle) заполнены поля, отмеченные как обязательные
         * в настройках dataFieldsSettings для переданного флага verify (резидент/нерезидент).
         *
         * @param {Object[]|Object} vehicles - Список ТС или объект, содержащий ТС.
         * @param {number} verify - Флаг резидентности/нерезидентности.
         * @returns {boolean} true, если все обязательные поля каждого ТС заполнены, иначе false.
         */
        static areAllVehiclesRequiredFieldsFilled(vehicles, verify) {
            if (!vehicles || !Array.isArray(vehicles)) {
                if (vehicles && Object.keys(vehicles).length > 0) {
                    vehicles = Object.values(vehicles)
                } else {
                    return false
                }
            }
            const requiredFieldsSettings = BpmOsgpoModule.Vehicle.dataFieldsSettings(verify);
            for (const vehicle of vehicles) {
                for (const fieldKey in requiredFieldsSettings) {
                    if (fieldKey in vehicle) {
                        const isFilled = BpmOsgpoModule.Validator.areAllRequiredFieldsFilled(vehicle[fieldKey], requiredFieldsSettings[fieldKey]);
                        if (!isFilled) {
                            return false;
                        }
                    } else {
                        return false;
                    }
                }
            }
            return true;
        }
        /**
         * Проверяет наличие всех обязательных секций клиента для текущего шага поиска.
         *
         * Значения step:
         *  - 1: Должны присутствовать секции: client, filter
         *  - 2: Должны присутствовать секции: client, filter, driver, document, location
         *
         * @param {number} step - Шаг сценария.
         * @param {Object} clientInfo - Данные клиента.
         * @returns {boolean} Результат проверки.
         */
        static hasAllRequiredClientSectionsForStep(step, clientInfo) {
            const requiredSections = {
                1: ['client', 'filter'],
                2: ['client', 'filter', 'driver', 'document', 'location']
            }

            return BpmOsgpoModule.Validator.hasAllRequiredKeys(clientInfo, requiredSections[step])
        }
        /**
         * Проверяет наличие всех обязательных разделов ТС для текущего шага поиска.
         *
         * Значения step:
         *  - 1: Требуются секции: engine, filter, vehicle
         *  - 2: Требуются секции: engine, filter, vehicle, registration
         *
         * @param {number} step - Шаг сценария.
         * @param {Object} vehicleInfo - Данные клиента/ТС.
         * @returns {boolean} Результат проверки.
         */
        static hasAllRequiredVehicleSectionsForStep(step, vehicleInfo) {
            const requiredSections = {
                1: ['engine', 'filter', 'vehicle'],
                2: ['engine', 'filter', 'vehicle', 'registration']
            };

            return BpmOsgpoModule.Validator.hasAllRequiredKeys(vehicleInfo, requiredSections[step]);
        }
        /**
         * Проверяет заполненность всех обязательных полей клиента (client) и возвращает результат с сообщением.
         * @param {Object} data - Данные клиента.
         * @returns {{result:boolean, message?:string}} Результат проверки.
         */
        static isAllClientRequiredFieldsFilled(data) {
            if (!data || data.client === undefined) return {result: false, message: ''}
            const checks = [
                BpmOsgpoModule.Validator.areAllClientRequiredFieldsCompleted([data]),
            ]
            if (checks.every((item) => item.result)) {
                return {result: true}
            } else {
                return {result: false, message: checks.find((item) => !item.result).message}
            }
        }
        /**
         * Проверяет заполненность всех обязательных полей клиента по настройкам шага фильтра.
         * @param {Object[]|Object} data - Список клиентов или объект с ними.
         * @returns {{result:boolean, message?:string}} Результат проверки.
         */
        static areAllClientRequiredFieldsCompleted(data) {
            if (!Array.isArray(data)) {
                data = Object.values(data);
            }
            for (let item of data) {
                if (!item || JSON.stringify(item) === '{}') { continue; }
                let fieldSettings = BpmOsgpoModule.Client.dataFieldsSettings(item.filter.resident, item.filter.person)
                if (!BpmOsgpoModule.Validator.areAllRequiredFieldsFilled(BpmOsgpoModule.Client.flattenToValues(item), fieldSettings)) {
                    return {result: false, message: 'Пожалуйста, заполните все обязательные поля, чтобы продолжить.'}
                }
            }
            return {result: true}
        }
        
        /**
         * Проверяет заполненность обязательных полей блока регистрации ТС.
         * При verify=false возвращает true (проверка не требуется).
         * @param {Object} data - Данные ТС.
         * @returns {boolean} Результат проверки.
         */
        static areAllVehicleRegistrationFieldsFilled(data) {
            if (!data || typeof data !== 'object' || !data.registration) {
                return false;
            }
            if (!data.verify) return true
            const registrationFields = [
                "number",
                "certificate",
                "date",
                "region_id",
                "big_city",
                "kato",
            ];
            for (let field of registrationFields) {
                if (data.registration[field] === null || data.registration[field] === undefined || data.registration[field] === '') {
                    return false;
                }
            }
            return true;
        }

        static canCalculate(policy, mode = null) {
            if (policy.calculationDate || (mode?.hasPremium?.(policy) ?? !!policy.premium)) {
                return false
            }
            return BpmOsgpoModule.Wizard.isReady(BpmOsgpoModule.Wizard.calculatePreconditionChecks(), policy, mode)
        }

        static canCheckout(policy, mode = null) {
            if (!policy.calculationDate && !(mode?.hasPremium?.(policy) ?? !!policy.premium)) {
                return false
            }
            return BpmOsgpoModule.Wizard.isReady(BpmOsgpoModule.Wizard.checkoutPreconditionChecks(), policy, mode)
        }

        static hasIncompleteParticipantProcesses(policy) {
            return BpmOsgpoModule.Wizard.hasIncompleteParticipantProcesses(policy)
        }

        static canProceedFromParticipantsStep(policy) {
            return BpmOsgpoModule.Wizard.canProceedFromParticipantsStep(policy)
        }

        static canProceedFromTreatyStep(policy) {
            return BpmOsgpoModule.Wizard.canProceedFromTreatyStep(policy)
        }

        static getMaxReachableWizardStep(policy) {
            return BpmOsgpoModule.Wizard.getMaxReachableWizardStep(policy)
        }

        static normalizeWizardStep(step, policy) {
            return BpmOsgpoModule.Wizard.normalizeWizardStep(step, policy)
        }

        static getWizardNextStepHint(step, policy, mode = null) {
            return BpmOsgpoModule.Wizard.getWizardNextStepHint(step, policy, mode)
        }
    }

    static Wizard = class {
        static stepsDictionary() {
            return [
                { id: 'participants', title: 'Участники',                  short: 'Участ.' },
                { id: 'treaty',       title: 'Условия договора',           short: 'Условия' },
                { id: 'summary',      title: 'Оплата и заключение',        short: 'Оплата' },
            ]
        }

        static get LAST_STEP() {
            return this.stepsDictionary().length - 1
        }

        static getStep(index) {
            return this.stepsDictionary()[index] ?? null
        }

        static hasIncompleteParticipantProcesses(policy) {
            const processes = policy?.business_process
            if (!Array.isArray(processes)) {
                return false
            }
            return processes.some(proc =>
                [1, 2, 3].includes(proc?.type) && proc?.process_data?.status !== 'COMPLETED'
            )
        }

        static hasActiveParticipantProcess(policy, type) {
            if (!Array.isArray(policy?.business_process)) {
                return false
            }
            return policy.business_process.some(proc =>
                proc?.type === type && BpmOsgpoModule.BusinessProcess.isActive(proc)
            )
        }

        static isProcessDataFailed(process) {
            return ['FAILED', 'TIME_OUT', 'TIMED_OUT'].includes(String(process?.process_data?.status ?? ''))
        }

        static isProcessWaitingUser(process) {
            return String(process?.process_data?.status ?? '') === 'WAITING_USER'
        }

        static waitingActionHintMessage(process) {
            const actionMessages = {
                kdp: 'Завершите отправку КДП',
                rules: 'Ознакомьтесь с правилами страхования',
                sign: 'Выберите способ подписания',
                payment: 'Выберите способ оплаты',
                phone: 'Подтвердите номер телефона',
            }
            const action = process?.process_data?.waiting_action
            if (action && actionMessages[action]) {
                return actionMessages[action]
            }
            return process?.process_data?.message || null
        }

        static getIncompleteBusinessProcesses(policy, { participantOnly = false } = {}) {
            if (!Array.isArray(policy?.business_process)) {
                return []
            }
            return policy.business_process.filter((proc) => {
                if (proc?.process_data?.status === 'COMPLETED') {
                    return false
                }
                if (participantOnly) {
                    return [1, 2, 3].includes(proc?.type)
                }
                return true
            })
        }

        static businessProcessHintMessage(policy, { participantOnly = false } = {}) {
            const incompleteProcesses = this.getIncompleteBusinessProcesses(policy, { participantOnly })
            const failedProcess = incompleteProcesses.find((proc) => this.isProcessDataFailed(proc))

            if (failedProcess) {
                return (participantOnly
                        ? 'В процессах проверки участников есть ошибки. Исправьте данные и повторите.'
                        : 'В процессах есть ошибки. Исправьте данные и повторите.')
            }

            const pendingConfirmationProcess = incompleteProcesses.find((proc) =>
                BpmOsgpoModule.BusinessProcess.isPendingConfirmation(proc)
            )
            if (pendingConfirmationProcess) {
                return participantOnly
                    ? 'Подтвердите поиск данных по участникам'
                    : 'Подтвердите поиск данных'
            }

            const waitingUserProcess = incompleteProcesses.find((proc) => this.isProcessWaitingUser(proc))
            if (waitingUserProcess) {
                return this.waitingActionHintMessage(waitingUserProcess)
                    || (participantOnly
                        ? 'Завершите действия по проверке участников'
                        : 'Требуется ваше действие для продолжения')
            }

            if (incompleteProcesses.length > 0) {
                return participantOnly
                    ? 'Дождитесь завершения получения данных'
                    : 'Дождитесь завершения получения данных'
            }

            return null
        }

        static findFirstFailedCheck(checks, policy, mode = null) {
            for (const check of checks) {
                if (!check.test(policy, mode)) {
                    return typeof check.message === 'function' ? check.message(policy, mode) : check.message
                }
            }
            return null
        }

        static isReady(checks, policy, mode = null) {
            return this.findFirstFailedCheck(checks, policy, mode) === null
        }

        static incompleteParticipantProcessMessage(policy) {
            return this.businessProcessHintMessage(policy, { participantOnly: true })
                || 'Дождитесь завершения получения данных'
        }

        static getParticipantClients(policy) {
            const clients = []
            if (policy?.insurer) {
                clients.push(policy.insurer)
            }
            if (Array.isArray(policy?.insureds)) {
                for (const insured of policy.insureds) {
                    if (insured && !clients.includes(insured)) {
                        clients.push(insured)
                    }
                }
            }
            return clients
        }

        static isDgpoForbidden(policy) {
            return this.getParticipantClients(policy).some(client =>
                Number(client?.podft?.code) > 1
            )
        }

        static hasClientBeingVerified(policy) {
            return this.getParticipantClients(policy).some(client => !!client?.beingVerify)
        }

        static findFailedClientVerify(policy) {
            return this.getParticipantClients(policy).find(client =>
                client?.podft && client.podft.success === false
            ) ?? null
        }

        static failedClientVerifyMessage(policy) {
            return this.findFailedClientVerify(policy)?.podft?.message || 'Проверка клиента не пройдена'
        }

        static clientVerifyChecks() {
            return [
                {
                    test: (policy) => !this.hasClientBeingVerified(policy),
                    message: 'Дождитесь завершения проверки клиента',
                },
                {
                    test: (policy) => !this.findFailedClientVerify(policy),
                    message: (policy) => this.failedClientVerifyMessage(policy),
                },
            ]
        }

        static getPolicyVehicles(policy) {
            return Array.isArray(policy?.vehicle) ? policy.vehicle.filter(Boolean) : []
        }

        static hasVehicleBeingVerified(policy) {
            return this.getPolicyVehicles(policy).some(vehicle => !!vehicle?.beingVerify)
        }

        static findFailedVehicleVerify(policy) {
            return this.getPolicyVehicles(policy).find(vehicle =>
                vehicle?.podft && vehicle.podft.success === false
            ) ?? null
        }

        static failedVehicleVerifyMessage(policy) {
            return this.findFailedVehicleVerify(policy)?.podft?.message || 'Проверка авто не пройдена'
        }

        static vehicleVerifyChecks() {
            return [
                {
                    test: (policy) => !this.hasVehicleBeingVerified(policy),
                    message: 'Дождитесь завершения проверки авто',
                },
                {
                    test: (policy) => !this.findFailedVehicleVerify(policy),
                    message: (policy) => this.failedVehicleVerifyMessage(policy),
                },
            ]
        }

        static participantProgressChecks() {
            return [
                {
                    test: (policy) => !!policy?.insurer || this.hasActiveParticipantProcess(policy, 1),
                    message: 'Добавьте страхователя',
                },
                {
                    test: (policy) => (Array.isArray(policy?.insureds) && policy.insureds.length > 0)
                        || this.hasActiveParticipantProcess(policy, 2),
                    message: 'Добавьте хотя бы одного водителя',
                },
                {
                    test: (policy) => (Array.isArray(policy?.vehicle) && policy.vehicle.length > 0)
                        || this.hasActiveParticipantProcess(policy, 3),
                    message: 'Добавьте хотя бы одно транспортное средство',
                },
                {
                    test: (policy) => !this.hasIncompleteParticipantProcesses(policy),
                    message: (policy) => this.incompleteParticipantProcessMessage(policy),
                },
                ...this.clientVerifyChecks(),
                ...this.vehicleVerifyChecks(),
            ]
        }

        static participantReadyChecks() {
            return [
                {
                    test: (policy) => !!policy?.insurer,
                    message: 'Добавьте страхователя',
                },
                {
                    test: (policy) => Array.isArray(policy?.insureds) && policy.insureds.length > 0,
                    message: 'Добавьте хотя бы одного водителя',
                },
                {
                    test: (policy) => Array.isArray(policy?.vehicle) && policy.vehicle.length > 0,
                    message: 'Добавьте хотя бы одно транспортное средство',
                },
                {
                    test: (policy) => !this.hasIncompleteParticipantProcesses(policy),
                    message: (policy) => this.incompleteParticipantProcessMessage(policy),
                },
                ...this.clientVerifyChecks(),
                ...this.vehicleVerifyChecks(),
            ]
        }

        static treatyChecks() {
            return [
                {
                    test: (policy) => !!policy?.treaty?.date,
                    message: 'Укажите дату начала договора',
                },
                {
                    test: (policy) => !!policy?.treaty?.term,
                    message: 'Укажите дату окончания договора',
                },
                {
                    test: (policy) => !!policy?.manager,
                    message: 'Выберите менеджера',
                },
            ]
        }

        static allBusinessProcessesCompletedCheck() {
            return {
                test: (policy) => !Array.isArray(policy?.business_process)
                    || policy.business_process.every(proc => proc?.process_data?.status === 'COMPLETED'),
                message: (policy) => this.businessProcessHintMessage(policy)
                    || 'Дождитесь завершения получения данных',
            }
        }

        static hasActiveCheckoutProcess(policy) {
            if (!Array.isArray(policy?.business_process)) {
                return false
            }
            return policy.business_process.some((proc) => {
                if (proc?.type !== 5) {
                    return false
                }
                if (BpmOsgpoModule.BusinessProcess.isCompleted(proc)) {
                    return false
                }
                if (this.isProcessDataFailed(proc)) {
                    return false
                }
                return true
            })
        }

        static checkoutProcessReadyCheck() {
            return {
                test: (policy) => !this.hasActiveCheckoutProcess(policy),
                message: (policy) => {
                    const checkoutProcess = (policy?.business_process ?? []).find(proc => proc?.type === 5)
                    if (!checkoutProcess) {
                        return 'Дождитесь завершения оформления полиса'
                    }
                    return this.waitingActionHintMessage(checkoutProcess)
                        || checkoutProcess?.process_data?.message
                        || 'Дождитесь завершения оформления полиса'
                },
            }
        }

        static calculatePreconditionChecks() {
            return [
                ...this.participantReadyChecks(),
                this.allBusinessProcessesCompletedCheck(),
                ...this.treatyChecks(),
            ]
        }

        static checkoutPreconditionChecks() {
            return [
                ...this.participantReadyChecks(),
                this.allBusinessProcessesCompletedCheck(),
                this.checkoutProcessReadyCheck(),
                ...this.treatyChecks(),
            ]
        }

        static getWizardNextStepHint(step, policy, mode = null) {
            const numericStep = Number(step)

            if (numericStep === 0) {
                const message = this.findFirstFailedCheck(this.participantProgressChecks(), policy, mode)
                return { ready: message === null, message }
            }

            if (numericStep === 1) {
                const message = this.findFirstFailedCheck(this.treatyChecks(), policy, mode)
                return { ready: message === null, message }
            }

            if (numericStep === 2) {
                const canCheckout = BpmOsgpoModule.Validator.canCheckout(policy, mode)
                const canCalculate = BpmOsgpoModule.Validator.canCalculate(policy, mode)
                if (canCheckout || canCalculate) {
                    return { ready: true, message: null }
                }
                const hasPremium = !!(policy?.calculationDate || (mode?.hasPremium?.(policy) ?? policy?.premium))
                const checks = hasPremium ? this.checkoutPreconditionChecks() : this.calculatePreconditionChecks()
                const message = this.findFirstFailedCheck(checks, policy, mode)
                    || (hasPremium
                        ? 'Заполните все обязательные данные для оформления'
                        : 'Заполните все обязательные данные для расчёта')
                return { ready: false, message }
            }

            return { ready: false, message: null }
        }

        static canProceedFromParticipantsStep(policy) {
            return this.isReady(this.participantProgressChecks(), policy)
        }

        static canProceedFromTreatyStep(policy) {
            return this.isReady(this.treatyChecks(), policy)
        }

        static getMaxReachableWizardStep(policy) {
            if (!this.canProceedFromParticipantsStep(policy)) {
                return 0
            }
            if (!this.canProceedFromTreatyStep(policy)) {
                return 1
            }
            return this.LAST_STEP
        }

        static normalizeWizardStep(step, policy) {
            const numeric = Number(step)
            let normalized = Number.isFinite(numeric)
                ? Math.min(Math.max(Math.trunc(numeric), 0), this.LAST_STEP)
                : 0
            const maxReachable = this.getMaxReachableWizardStep(policy)
            if (normalized > maxReachable) {
                normalized = maxReachable
            }
            return normalized
        }
    }
}
